import React, { useState } from "react";
import { toast } from "sonner";
import { MessageSquare, X } from "lucide-react";
import { api } from "@/lib/api";
import { Stars } from "@/pages/Purchases";

function avatarFor(name) {
  return "https://api.dicebear.com/7.x/initials/svg?seed=" + encodeURIComponent(name || "Buyer");
}

function ReviewRow({ review, canReply, onChange }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(review.reply?.text || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!text.trim()) { toast.error("Write a reply first"); return; }
    setSaving(true);
    try {
      await api.post(`/reviews/${review.id}/reply`, { text });
      toast.success("Reply posted");
      setOpen(false);
      onChange && onChange();
    } catch (err) { toast.error(err.message || "Could not post reply"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your reply?")) return;
    try { await api.del(`/reviews/${review.id}/reply`); toast.success("Reply removed"); onChange && onChange(); }
    catch (err) { toast.error(err.message || "Could not remove reply"); }
  };

  return (
    <div style={{ display: "flex", gap: 12 }} data-testid={`review-${review.id}`}>
      <img src={review.buyer_avatar || avatarFor(review.buyer_name)} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover" }} />
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 700 }}>{review.buyer_name || "Buyer"}</span>
          <Stars value={review.rating} size={13} />
        </div>
        {review.item_title && <div className="ss-muted" style={{ fontSize: 12, marginTop: 2 }}>on {review.item_title}</div>}
        {review.text && <div style={{ fontSize: 14, marginTop: 6, color: "var(--text)" }}>{review.text}</div>}
        {review.photo && (
          <img src={review.photo} alt="review" onClick={() => window.open(review.photo, "_blank")} data-testid={`review-photo-${review.id}`}
            style={{ marginTop: 8, width: 120, height: 120, objectFit: "cover", borderRadius: 12, cursor: "zoom-in", border: "1px solid var(--line, #e5e1d5)" }} />
        )}

        {review.reply?.text && (
          <div style={{ marginTop: 10, marginLeft: 4, paddingLeft: 12, borderLeft: "3px solid var(--gold)" }} data-testid={`review-reply-${review.id}`}>
            <div className="ss-muted" style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>Seller's response</div>
            <div style={{ fontSize: 14 }}>{review.reply.text}</div>
          </div>
        )}

        {canReply && !open && (
          <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ marginTop: 8 }} onClick={() => { setText(review.reply?.text || ""); setOpen(true); }} data-testid={`reply-btn-${review.id}`}>
            <MessageSquare size={13} /> {review.reply?.text ? "Edit reply" : "Reply"}
          </button>
        )}
        {canReply && review.reply?.text && !open && (
          <button className="ss-btn ss-btn-ghost ss-btn-sm" style={{ marginTop: 8, marginLeft: 8, color: "var(--red)" }} onClick={remove} data-testid={`reply-delete-${review.id}`}>
            <X size={13} /> Remove
          </button>
        )}

        {canReply && open && (
          <div style={{ marginTop: 8 }}>
            <div className="ss-field" style={{ height: "auto", padding: 10, alignItems: "flex-start" }}>
              <textarea rows={2} placeholder="Reply publicly to this review…" value={text} onChange={(e) => setText(e.target.value)} maxLength={500}
                style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "vertical", fontFamily: "var(--font-body)", fontSize: 14, color: "var(--text)" }}
                data-testid={`reply-input-${review.id}`} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="ss-btn ss-btn-primary ss-btn-sm" disabled={saving} onClick={submit} data-testid={`reply-submit-${review.id}`}>{saving ? "Posting…" : "Post reply"}</button>
              <button className="ss-btn ss-btn-ghost ss-btn-sm" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SellerReviews({ reviews = [], canReply = false, onChange }) {
  if (!reviews.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="store-reviews">
      {reviews.map((r) => (
        <ReviewRow key={r.id} review={r} canReply={canReply} onChange={onChange} />
      ))}
    </div>
  );
}
